/**
 * Settings API Client
 * Phase 8: Settings Management
 * Updated: Fixed API paths with /api prefix
 */

import apiClient from "./client";
import type {
  BookingRuleDto,
  BusinessType,
  BusinessTypeFormData,
  ClinicSettings,
  ClinicSettingsUpdateRequest,
  GeneralSettings,
  GeneralSettingsUpdateRequest,
  OperatingModeDto,
  UserProfile,
} from "@/types/settings";

/**
 * User Profile API
 */
export const userApi = {
  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<UserProfile> => {
    const response = await apiClient.get<UserProfile>("/api/users/me");
    return response.data;
  },
};

/**
 * General Settings API
 * Phase 1: Landing image/ment, sync settings
 */
export const generalSettingsApi = {
  /**
   * Get general settings
   */
  get: async (): Promise<GeneralSettings> => {
    const response = await apiClient.get<GeneralSettings>("/api/settings/me");
    return response.data;
  },

  /**
   * Update general settings
   */
  update: async (
    data: GeneralSettingsUpdateRequest,
  ): Promise<GeneralSettings> => {
    const response = await apiClient.put<GeneralSettings>(
      "/api/settings/me",
      data,
    );
    return response.data;
  },
};

/**
 * Business Type API
 */
export const businessTypeApi = {
  /**
   * Get all business types
   */
  getAll: async (): Promise<BusinessType[]> => {
    const response = await apiClient.get<BusinessType[]>("/api/business-types");
    return response.data;
  },

  /**
   * Get business type by ID
   */
  getById: async (id: number): Promise<BusinessType> => {
    const response = await apiClient.get<BusinessType>(
      `/api/business-types/${id}`,
    );
    return response.data;
  },

  /**
   * Get business type by code
   */
  getByCode: async (code: string): Promise<BusinessType> => {
    const response = await apiClient.get<BusinessType>(
      `/api/business-types/code/${code}`,
    );
    return response.data;
  },

  /**
   * Create business type (ADMIN only)
   */
  create: async (data: BusinessTypeFormData): Promise<BusinessType> => {
    const response = await apiClient.post<BusinessType>(
      "/api/admin/business-types",
      data,
    );
    return response.data;
  },

  /**
   * Update business type (ADMIN only)
   */
  update: async (
    id: number,
    data: BusinessTypeFormData,
  ): Promise<BusinessType> => {
    const response = await apiClient.put<BusinessType>(
      `/api/admin/business-types/${id}`,
      data,
    );
    return response.data;
  },

  /**
   * Delete business type (ADMIN only)
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/admin/business-types/${id}`);
  },
};

/**
 * Clinic Settings API
 */
export const clinicSettingsApi = {
  /**
   * Get clinic settings
   */
  get: async (): Promise<ClinicSettings> => {
    const response = await apiClient.get<ClinicSettings>(
      "/api/settings/clinic/me",
    );
    return response.data;
  },

  /**
   * Update clinic settings (ADMIN only)
   */
  update: async (
    data: ClinicSettingsUpdateRequest,
  ): Promise<ClinicSettings> => {
    const response = await apiClient.put<ClinicSettings>(
      "/api/settings/clinic",
      data,
    );
    return response.data;
  },
};

/**
 * Operating Mode API
 */
export const operatingModeApi = {
  /**
   * Get operating mode (POWER/ADMIN)
   */
  get: async (): Promise<OperatingModeDto> => {
    const response = await apiClient.get<OperatingModeDto>(
      "/api/settings/operating-mode",
    );
    return response.data;
  },

  /**
   * Set operating mode (POWER/ADMIN)
   */
  set: async (mode: string): Promise<void> => {
    await apiClient.put("/api/settings/operating-mode", { mode });
  },
};

/**
 * 설정 변경 이력 타입
 */
export interface SettingsChangeHistory {
  id: number;
  userId: number;
  username: string;
  userName: string;
  entityType:
    | "SETTINGS"
    | "CLINIC_SETTINGS"
    | "ORGANIZATION_PROMPT"
    | "ORGANIZATION"
    | "MEDICAL_STAFF"
    | "BOOKING_SLOT_OVERRIDE"
    | "SERVICE_ITEM"
    | "BOOKING_RULE"
    | "DEPARTMENT";
  entityTypeName: string;
  entityId: number;
  fieldName: string;
  fieldNameKorean: string;
  oldValue: string | null;
  newValue: string | null;
  changeType: "CREATE" | "UPDATE" | "DELETE";
  changeTypeName: string;
  entityName: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

/**
 * Settings Change History API (V189)
 * 설정 변경 이력 조회 - 고객 클레임 대응용
 */
export const settingsChangeHistoryApi = {
  /**
   * 설정 변경 이력 조회 (페이징)
   */
  getHistory: async (
    page = 0,
    size = 20,
    entityType?: string,
    startDate?: string,
    endDate?: string,
  ): Promise<PageResponse<SettingsChangeHistory>> => {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("size", String(size));
    if (entityType) params.append("entityType", entityType);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const response = await apiClient.get<PageResponse<SettingsChangeHistory>>(
      `/api/settings/change-history?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * 기간별 변경 이력 조회
   */
  getHistoryByDateRange: async (
    startDate: string,
    endDate: string,
  ): Promise<SettingsChangeHistory[]> => {
    const response = await apiClient.get<SettingsChangeHistory[]>(
      `/api/settings/change-history/by-date?startDate=${startDate}&endDate=${endDate}`,
    );
    return response.data;
  },

  /**
   * 최근 변경 이력 조회 (최근 20건)
   */
  getRecentHistory: async (): Promise<SettingsChangeHistory[]> => {
    const response = await apiClient.get<SettingsChangeHistory[]>(
      "/api/settings/change-history/recent",
    );
    return response.data;
  },
};

/**
 * BL-139: 특정 날짜 휴무일 API
 */
export interface HospitalHolidayDto {
  id: number;
  date: string; // YYYY-MM-DD
  reason: string;
  createdAt: string;
}

export const hospitalHolidaysApi = {
  /** 전체 휴무일 목록 조회 */
  getAll: async (): Promise<HospitalHolidayDto[]> => {
    const response = await apiClient.get<HospitalHolidayDto[]>(
      "/api/settings/holidays",
    );
    return response.data;
  },

  /** 특정 날짜 휴무일 추가 */
  add: async (date: string, reason?: string): Promise<HospitalHolidayDto> => {
    const response = await apiClient.post<HospitalHolidayDto>(
      "/api/settings/holidays",
      { date, reason: reason || "" },
    );
    return response.data;
  },

  /** 휴무일 삭제 */
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/settings/holidays/${id}`);
  },

  /** 특정 날짜 휴무 여부 확인 */
  check: async (date: string): Promise<boolean> => {
    const response = await apiClient.get<{ date: string; isHoliday: boolean }>(
      `/api/settings/holidays/check?date=${date}`,
    );
    return response.data.isHoliday;
  },
};

/**
 * V1069: Booking Rules API
 * booking_rules 테이블에서 예약 규칙 조회/수정
 * Single Source of Truth로 hospitalInfo.bookingRules 대체
 */
export const bookingRulesApi = {
  /**
   * Get booking rules for current organization
   */
  get: async (): Promise<BookingRuleDto> => {
    const response = await apiClient.get<BookingRuleDto>(
      "/api/booking-rules/me",
    );
    return response.data;
  },

  /**
   * Update booking rules for current organization
   */
  update: async (data: BookingRuleDto): Promise<BookingRuleDto> => {
    const response = await apiClient.put<BookingRuleDto>(
      "/api/booking-rules/me",
      data,
    );
    return response.data;
  },
};
